import NAICS from '@aibees/elephant/constants/naics.json';
import { NaicsLink } from '../components/naics-link';
import {
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';

export default function sicAndNaicsFilterConfig(): SearchFilterBase {
  const sicCodesRequest: SearchFilter = {
    name: 'SIC',
    type: SearchFilterType.NONE,
    index: 2,
    key: 'sic',
    field: 'sic',
    comingSoon: true,
    disabled: true,
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'SIC',
        },
      },
    },
    sections: { main: 3, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: '',
        key: '',
        metadata: {
          delimiter: '::',
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Enter SIC Code or Industry Type, (OR) Search',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: ``,
            label: '',
            icon: null,
            index: undefined,
            queryKeys: [''],
          },
        ],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 1,
        layer: 4,
        key: '',
        field: '',
        metadata: {
          delimiter: '::',
          color: 'error',
          icon: 'block',
          placeholder: 'Enter SIC Code or Industry Type, (OR) Search',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: ``,
            label: '',
            icon: null,
            index: undefined,
            queryKeys: [''],
          },
        ],
      },
      {
        name: 'SIC Lists',
        type: SearchFilterType.CUSTOM_COMPONENT,
        index: 2,
        layer: 4,
        field: 'sic_lists',
        key: 'sic_lists',
        customComponent: <></>,
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 3,
        layer: 4,
        field: '',
        key: '',
        metadata: {
          delimiter: '::',
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Enter SIC Code or Industry Type, (AND) Search',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        options: [
          {
            type: 'URL',
            value: ``,
            label: '',
            icon: null,
            index: undefined,
            queryKeys: [''],
          },
        ],
      },
    ],
  };
  const naicsCodesRequest: SearchFilter = {
    name: 'NAICS',
    type: SearchFilterType.NONE,
    index: 3,
    key: 'naics',
    field: 'naics',
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'NAICS',
        },
      },
    },
    sections: { main: 3, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'naics_include_any',
        key: 'naics_include_any',
        metadata: {
          delimiter: '::',
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Enter NAICS Code or Industry Type, (OR) Search',
          autocomplete: {
            customOptions: true,
            customOptionsLimit: 20,
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        treeOptions: [NAICS],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 1,
        layer: 4,
        key: 'naics_exclude_any',
        field: 'naics_exclude_any',
        metadata: {
          delimiter: '::',
          color: 'error',
          icon: 'block',
          placeholder: 'Enter NAICS Code or Industry Type, (OR) Search',
          autocomplete: {
            customOptions: true,
            customOptionsLimit: 20,
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        treeOptions: [NAICS],
      },
      {
        name: 'NAICS Lists',
        type: SearchFilterType.CUSTOM_COMPONENT,
        index: 2,
        layer: 4,
        field: 'naics_lists',
        key: 'naics_lists',
        customComponent: <NaicsLink />,
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 3,
        layer: 4,
        field: 'naics_include_and',
        key: 'naics_include_and',
        metadata: {
          delimiter: '::',
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Enter NAICS Code or Industry Type, (AND) Search',
          autocomplete: {
            customOptions: true,
            customOptionsLimit: 20,
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        searchKey: 'value',
        config: { labelKey: 'label', valueKey: 'value' },
        treeOptions: [NAICS],
      },
    ],
  };

  return {
    name: `SIC & NAICS`,
    icon: '10-plus',
    index: 11,
    type: SearchFilterType.ROOT,
    pinned: true,
    category: [FilterCategory.COMPANY_DATA],
    layer: 3,
    items: [
      {
        name: '',
        type: SearchFilterType.BUTTON_GROUP,
        icon: undefined,
        index: 0,
        key: '',
        field: '',
        metadata: {
          allowParallelItems: true,
          tabs: {
            defaultOpenIndex: 1,
          },
        },
        config: { labelKey: 'label', valueKey: 'value' },
        items: [
          ...[
            {
              name: 'Applied Filters',
              type: SearchFilterType.APPLIED_FILTERS,
              index: 2,
              icon: undefined,
              key: '',
              field: '',
              metadata: {},
            },
          ],
          ...[sicCodesRequest, naicsCodesRequest],
        ],
      },
    ],
  };
}
